import { convertLegacyPlugin } from '@backstage/core-compat-api';

import { adrPlugin } from '@backstage-community/plugin-adr';
import { githubActionsPlugin } from '@backstage-community/plugin-github-actions';
import { vaultPlugin } from '@backstage-community/plugin-vault';
import { kubernetesPlugin } from '@backstage/plugin-kubernetes';
import { signalsPlugin } from '@backstage/plugin-signals';
import { sonarQubePlugin } from '@fabz26/plugin-sonarqube';
import { pagerDutyPlugin } from '@pagerduty/backstage-plugin';
import { githubInsightsPlugin } from '@roadiehq/backstage-plugin-github-insights';
import { githubPullRequestsPlugin } from '@roadiehq/backstage-plugin-github-pull-requests';
import { jiraPlugin } from '@roadiehq/backstage-plugin-jira';
import { accentuatePlugin } from '@tranzact/backstage-plugin-aceituna';
import { octopusDeployPlugin } from '@tranzact/plugin-octopus-deploy';

/**
 * Legacy plugins that have to be registered by hand, because nothing else in
 * the app registers them.
 *
 * `convertLegacyAppRoot` only discovers a legacy plugin when one of its
 * components is the `element` of a Route inside FlatRoutes, or a child of such
 * a Route, or part of the `entityPage` option -- which we do not pass, since
 * our entity page is an ordinary route. It does not walk the app root wrapper,
 * and it only *clones* the `<Root>` sidebar as a layout override rather than
 * traversing it. A plugin it misses keeps its components but loses its API
 * factories, and throws "No implementation available for apiRef{...}" the
 * moment one of them renders.
 *
 * Two ways in:
 *  - consumed from EntityPage.tsx, which the shim never traverses;
 *  - consumed outside the route tree entirely. `signals` is the clearest case:
 *    upstream's `<SignalsDisplay />` renders nothing at all and exists purely
 *    so that the legacy app would install the plugin.
 *
 * A plugin only belongs here if nothing else provides its APIs. `tech-radar`
 * looks like it qualifies -- /tech-radar renders our own TechRadarCustomPage
 * wrapper rather than the plugin's page, so the plugin is never discovered --
 * but apis.ts already provides techRadarApiRef with our own client. Registering
 * the plugin as well is an API_FACTORY_CONFLICT, because the two factories come
 * from different plugin ids, and the app refuses to start.
 *
 * Adding a plugin to EntityPage.tsx means adding it here too;
 * legacyPlugins.test.ts fails if you forget, and also fails if what you add
 * collides with an API the app provides itself.
 */
const legacyPlugins = [
  // Consumed from EntityPage.tsx. aceituna's wrapper is the EntityLayoutWrapper,
  // so it wraps every entity in the catalog rather than one tab.
  accentuatePlugin,
  adrPlugin,
  githubActionsPlugin,
  githubInsightsPlugin,
  githubPullRequestsPlugin,
  jiraPlugin,
  kubernetesPlugin,
  octopusDeployPlugin,
  pagerDutyPlugin,
  sonarQubePlugin,
  vaultPlugin,
  // Consumed outside the route tree.
  signalsPlugin,
];

/**
 * These carry no extensions of their own -- the components they belong to are
 * still rendered by the legacy tree that `convertLegacyAppRoot` produces. All
 * we want from the conversion is the plugin's API factories.
 */
export const legacyPluginFeatures = legacyPlugins.map(plugin =>
  convertLegacyPlugin(plugin, { extensions: [] }),
);
